/**
 * Better way to handle ready and resize events:
 * Register your handler and thats it
 *
 * @type {{registerReadyHandler, registerUiComponent, registerResizeHandler, trigger}}
 *
 * @author Frank Kubis <frank.kubis@tu-dresden.de>
 */

import $ from 'jquery';

var App = (function ($) {
    "use strict";

    var Handler = function () {

        // List of handlers that will be triggered after DOM finished loading
        var readyHandlers  = [];
        // List of handlers that will be triggered on every DOM resize
        var resizeHandlers = [];
        // List of ui components, will triggered at DOM finished loading and can be triggered manuel with ui
        var uiComponents   = [];

        var allHandlers = [readyHandlers, resizeHandlers, uiComponents];

        /**
         * Get the list or -1 by the name of it
         *
         * @param {String} name of list
         *
         * returns {*}
         */
        var getHandlersByName = function (name) {
            var list;
            switch (name) {
                case "ready" :
                    list = readyHandlers;
                    break;
                case "resize":
                    list = resizeHandlers;
                    break;
                case "ui":
                    list = uiComponents;
                    break;
                default:
                    list = -1;
                    break;
            }

            return list;
        };

        /**
         * Test for trigger name is part of list
         *
         * @param {String} name of trigger
         * @param {List}   list to look for
         *
         * @returns {boolean}
         */
        var isNameInList = function (list, name) {
            return ($.grep(list, function (obj) {
                    return (obj.name === name);
                })).length > 0;
        };

        /**
         * Get the position of the trigger from list. -1 if not part of it
         *
         * @param {String} name of trigger
         * @param {List}   list to look for
         *
         * @returns {number}
         */
        var getPositionInList = function (list, name) {
            var pos = -1;
            $.each(list, function (index, value) {
                if (value.name === name) {
                    pos = index;
                }
            });

            return pos;
        };

        /**
         * Call all handlers of given list
         *
         * @param {List} handlers: List of handlers
         */
        var call = function (handlers) {
            var fifo = [];
            $.each(handlers, function (index, handler) {
                var name   = handler.name;
                var inFifo = isNameInList(fifo, name);
                // Add handler to call stack if it is not already
                if (!inFifo) {
                    fifo.push(handler);
                }
                // Add dependencies to call stack
                if (handler.dependencies.length > 0) {
                    var reorder = function (dependencies) {
                        $.each(dependencies, function (index, value) {
                            var pos = getPositionInList(handlers, value);
                            var dep = null;

                            if (pos > -1) {
                                dep = handlers[getPositionInList(handlers, value)];
                            }

                            // Not found at current handler list, maybe its stored in other list?
                            var i = 0;
                            while (pos == -1) {
                                var tempHandler = allHandlers[i];
                                pos             = getPositionInList(tempHandler, value);
                                if (pos > -1) {
                                    dep = tempHandler[getPositionInList(tempHandler, value)];
                                }

                                // last handler checked, stop loop
                                if (i == allHandlers.length - 1) {
                                    break;
                                }

                                i++;
                            }
                            if (pos > -1) {
                                var depInFifo = isNameInList(fifo, dep.name);
                                // The dependency is already at stack, we need to move it at the end
                                if (depInFifo) {
                                    var depPos = getPositionInList(fifo, dep.name);
                                    fifo.splice(depPos, 1);
                                    fifo.push(dep);
                                } else {
                                    fifo.push(dep);
                                }
                                if (dep.dependencies.length > 0)
                                    reorder(dep.dependencies);
                            }
                        });
                    };
                    reorder(handler.dependencies);
                }
            });
            $.each(fifo, function (index, handler) {
                handler.handler.call();
            });
        };

        /**
         * Triggers an handler with given name in list.
         *
         * @param {String} name     of handler to trigger
         * @param {List}   handlers handlers where the name should searched
         *
         * @returns {boolean} True if found and triggered, false otherwise.
         */
        var trigger = function (name, handlers, ignoreDep) {
            var ret                = false;
            var ignoreDependencies = typeof ignoreDep == "boolean" ? ignoreDep : false;
            $.each(handlers, function (index, handler) {
                if (handler.name == name) {
                    // Triggers the callback function of this found handler
                    handler.handler.call();
                    // Trigger all dependencies
                    if (!ignoreDependencies) {
                        $.each(handler.dependencies, function (depIndex, dep) {
                            trigger(dep, handlers);
                        });
                    }

                    ret = true;
                }
            });

            return ret;
        };

        /**
         * Trigger an specific handler with given name
         *
         * The first handler of stored handlers with matched name will triggered. The order of searched handlers is
         * ui > ready > resize
         *
         * @param name Unique name of handler
         */
        var triggerName = function (name, ignoreDep) {
            if (name == "ui") {
                call(uiComponents);
            } else if (!trigger(name, uiComponents, ignoreDep)) {
                if (!trigger(name, readyHandlers, ignoreDep)) {
                    if (!trigger(name, resizeHandlers, ignoreDep)) {
                        console.log("Error: no handler with name " + name + " found");
                    }
                }
            }
        }

        /**
         * Save an new handler to list
         *
         * @param {List}     list         Where save the handler
         * @param {String}   name         Unique name of handler
         * @param {Function} handler      Callback witch will be triggered
         * @param {List}     dependencies Name of handlers that should be after this handler.
         */
        var push = function (list, name, handler, dependencies) {
            var tmp = {};
            // No overloading for js ;( quick and dirty workaround
            if (typeof name == "function") {
                tmp = {
                    "name": "",
                    "handler": name,
                    "dependencies": []
                };

            } else {
                tmp = {
                    "name": name,
                    "handler": handler,
                    "dependencies": $.isArray(dependencies) ? dependencies : []
                };
            }
            list.push(tmp);

        };

        // Public functions at this scope
        return {
            runReadyHandlers: function () {
                call(uiComponents);
                call(readyHandlers);
            },
            runResizeHandlers: function () {
                call(resizeHandlers);
            },
            runUiHandlers: function () {
                call(uiComponents);
            },
            /**
             * Register a new handler to any list
             *
             * @param {String|List} handlers     List of names or name of where the handler should registered.
             * @param {String}      name         Unique name of handler
             * @param {Function}    handler      Callback witch will be triggered
             * @param {List}        dependencies Name of handlers that should be after this handler.
             */
            register: function (handlers, name, handler, dependencies) {
                var registerAt = function (listName) {
                    var list = getHandlersByName(listName);
                    if (list == -1) {
                        console.log("Error: no list with name " + name + "found");
                    } else {
                        push(list, name, handler, dependencies);
                    }
                };
                if ($.isArray(handlers)) {
                    $.each(handlers, function (index, value) {
                        registerAt(value);
                    });
                } else if (typeof handlers == "string") {
                    registerAt(handlers);
                } else {
                    console.log("Error: unsupported type of handlers")
                }
            },
            /**
             * Registers a new ready handler
             *
             * @param {String}   name         Unique name of handler
             * @param {Function} handler      Callback witch will be triggered
             * @param {List}     dependencies Name of handlers that should be after this handler.
             */
            registerReadyHandler: function (name, handler, dependencies) {
                Handler.register("ready", name, handler, dependencies);

                // the dom could be loaded already
                if (ready) {
                    triggerName(name);
                }
            },
            /**
             * Register a new resize handler
             *
             * @param {String}   name         Unique name of handler
             * @param {Function} handler      Callback witch will be triggered
             * @param {List}     dependencies Name of handlers that should be after this handler.
             */
            registerResizeHandler: function (name, handler, dependencies) {
                Handler.register("resize", name, handler, dependencies);
            },
            /**
             * Register a new ui component
             *(typeof handlers == "string") {
                     re
             * @param {String}   name         Unique name of handler
             * @param {Function} handler      Callback witch will be triggered
             * @param {List}     dependencies Name of handlers that should be after this handler.
             */
            registerUiComponent: function (name, handler, dependencies) {
                Handler.register("ui", name, handler, dependencies);

                // the dom could be loaded already
                if (ready) {
                    triggerName(name);
                }
            },
            /**
             * Trigger an specific handler with given name
             *
             * The first handler of stored handlers with matched name will triggered. The order of searched handlers is
             * ui > ready > resize
             *
             * @param name Unique name of handler
             */
            trigger: function (name, ignoreDep) {
                triggerName(name, ignoreDep);
            }
        };
    }();

    var ready = false;

    // Do the jquery events
    $(function () {
        Handler.runReadyHandlers();
        ready = true;
    });

    var resizeEnd = function () {
        Handler.runResizeHandlers();
    };

    $(document).ajaxComplete(function () {
        Handler.runUiHandlers();
    });

    var timeout;
    $(window).on("resize orientationchange", function () {
        if (!ready)
            return;

        clearTimeout(timeout);
        timeout = setTimeout(resizeEnd, 100);
    });

    // Making some of the functions public to other files
    return {
        "registerReadyHandler": Handler.registerReadyHandler,
        "registerResizeHandler": Handler.registerResizeHandler,
        "registerUiComponent": Handler.registerUiComponent,
        "trigger": Handler.trigger
    };
})(jQuery);

// provide App in global context for now
window.App = App;

export default App;
